import React, { useEffect, useRef, useState } from 'react'
import { logger } from '@/utils/logger'

interface SimpleMinimapProps {
  viewer: any
  imageId: string
  bounds?: {
    x: number
    y: number
    width: number
    height: number
  }
  dimensions: {
    width: number
    height: number
  }
}

const SimpleMinimap: React.FC<SimpleMinimapProps> = ({ viewer, imageId, bounds, dimensions }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [thumbnail, setThumbnail] = useState<HTMLImageElement | null>(null)
  const [viewportRect, setViewportRect] = useState({ x: 0, y: 0, width: 0, height: 0 })

  // Fixed minimap size
  const MINIMAP_WIDTH = 220
  const MINIMAP_HEIGHT = 160

  // Load thumbnail image (lowest level)
  useEffect(() => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    // Load level 0 (lowest resolution) as thumbnail
    const thumbnailUrl = `/api/v1/imaging/standalone/dzi/${imageId}_files/0/0_0.jpeg`
    logger.debug('Minimap: Loading thumbnail', { thumbnailUrl })
    img.src = thumbnailUrl

    img.onload = () => {
      logger.debug('Minimap: Thumbnail loaded', { width: img.width, height: img.height })
      setThumbnail(img)
    }

    img.onerror = (e) => {
      logger.error('Minimap: Failed to load thumbnail', { thumbnailUrl, error: e })
      // Try alternative: use lowest DZI level instead
      const altUrl = `/api/v1/imaging/standalone/dzi/${imageId}_files/1/0_0.jpeg`
      logger.debug('Minimap: Trying alternative level 1', { altUrl })
      const altImg = new Image()
      altImg.crossOrigin = 'anonymous'
      altImg.src = altUrl
      altImg.onload = () => {
        logger.debug('Minimap: Alternative thumbnail loaded', { width: altImg.width, height: altImg.height })
        setThumbnail(altImg)
      }
      altImg.onerror = () => {
        logger.error('Minimap: Alternative thumbnail also failed')
      }
    }
  }, [imageId])

  // Update viewport rectangle when viewer changes
  useEffect(() => {
    if (!viewer) {
      logger.debug('Minimap: Viewer not ready')
      return
    }

    const updateViewport = () => {
      const vp = viewer.viewport
      if (!vp) {
        logger.debug('Minimap: Viewport not ready')
        return
      }

      // Get viewport bounds in image coordinates
      const topLeft = vp.viewportToImageCoordinates(vp.getBounds().getTopLeft())
      const bottomRight = vp.viewportToImageCoordinates(vp.getBounds().getBottomRight())

      // Normalize to 0-1 range
      const x = topLeft.x / dimensions.width
      const y = topLeft.y / dimensions.height
      const width = (bottomRight.x - topLeft.x) / dimensions.width
      const height = (bottomRight.y - topLeft.y) / dimensions.height

      logger.debug('Minimap: Viewport updated', {
        x: x.toFixed(3),
        y: y.toFixed(3),
        w: width.toFixed(3),
        h: height.toFixed(3)
      })
      setViewportRect({ x, y, width, height })
    }

    logger.debug('Minimap: Setting up viewport tracking')
    updateViewport()

    // Listen to viewport changes
    viewer.addHandler('animation', updateViewport)
    viewer.addHandler('zoom', updateViewport)
    viewer.addHandler('pan', updateViewport)

    return () => {
      if (viewer) {
        logger.debug('Minimap: Cleaning up viewport tracking')
        viewer.removeHandler('animation', updateViewport)
        viewer.removeHandler('zoom', updateViewport)
        viewer.removeHandler('pan', updateViewport)
      }
    }
  }, [viewer, dimensions])

  // Draw minimap
  useEffect(() => {
    if (!canvasRef.current || !thumbnail) {
      if (!thumbnail) logger.debug('Minimap: Waiting for thumbnail...')
      return
    }

    logger.debug('Minimap: Drawing minimap', { viewportRect })

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Clear canvas
    ctx.clearRect(0, 0, MINIMAP_WIDTH, MINIMAP_HEIGHT)

    // Calculate scale and crop for tissue area only
    let srcX = 0
    let srcY = 0
    let srcWidth = thumbnail.width
    let srcHeight = thumbnail.height

    if (bounds) {
      // Calculate tissue area in thumbnail coordinates
      // Thumbnail is level 0, so scale factor is dimensions/thumbnail.width
      const scaleX = thumbnail.width / dimensions.width
      const scaleY = thumbnail.height / dimensions.height

      srcX = bounds.x * scaleX
      srcY = bounds.y * scaleY
      srcWidth = bounds.width * scaleX
      srcHeight = bounds.height * scaleY
    }

    // Calculate destination size maintaining aspect ratio
    const aspectRatio = srcWidth / srcHeight
    let destWidth = MINIMAP_WIDTH
    let destHeight = MINIMAP_WIDTH / aspectRatio

    if (destHeight > MINIMAP_HEIGHT) {
      destHeight = MINIMAP_HEIGHT
      destWidth = MINIMAP_HEIGHT * aspectRatio
    }

    // Center the image
    const offsetX = (MINIMAP_WIDTH - destWidth) / 2
    const offsetY = (MINIMAP_HEIGHT - destHeight) / 2

    // Draw thumbnail (cropped to tissue area)
    ctx.drawImage(
      thumbnail,
      srcX, srcY, srcWidth, srcHeight,  // Source (tissue area)
      offsetX, offsetY, destWidth, destHeight  // Destination (centered)
    )

    // Draw viewport rectangle
    if (bounds) {
      // Calculate viewport rectangle position relative to tissue bounds
      const vpX = ((viewportRect.x - (bounds.x / dimensions.width)) / (bounds.width / dimensions.width)) * destWidth + offsetX
      const vpY = ((viewportRect.y - (bounds.y / dimensions.height)) / (bounds.height / dimensions.height)) * destHeight + offsetY
      const vpWidth = (viewportRect.width / (bounds.width / dimensions.width)) * destWidth
      const vpHeight = (viewportRect.height / (bounds.height / dimensions.height)) * destHeight

      ctx.strokeStyle = '#3B82F6'
      ctx.lineWidth = 2
      ctx.fillStyle = 'rgba(59, 130, 246, 0.15)'
      ctx.fillRect(vpX, vpY, vpWidth, vpHeight)
      ctx.strokeRect(vpX, vpY, vpWidth, vpHeight)
    } else {
      // No bounds, use full image
      const vpX = viewportRect.x * destWidth + offsetX
      const vpY = viewportRect.y * destHeight + offsetY
      const vpWidth = viewportRect.width * destWidth
      const vpHeight = viewportRect.height * destHeight

      ctx.strokeStyle = '#3B82F6'
      ctx.lineWidth = 2
      ctx.fillStyle = 'rgba(59, 130, 246, 0.15)'
      ctx.fillRect(vpX, vpY, vpWidth, vpHeight)
      ctx.strokeRect(vpX, vpY, vpWidth, vpHeight)
    }
  }, [thumbnail, viewportRect, bounds, dimensions])

  // Handle click to navigate
  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!viewer || !canvasRef.current) return

    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const clickY = e.clientY - rect.top

    // Calculate thumbnail display area
    const thumbnailWidth = thumbnail?.width || dimensions.width
    const thumbnailHeight = thumbnail?.height || dimensions.height

    let srcWidth = thumbnailWidth
    let srcHeight = thumbnailHeight

    if (bounds) {
      const scaleX = thumbnailWidth / dimensions.width
      const scaleY = thumbnailHeight / dimensions.height
      srcWidth = bounds.width * scaleX
      srcHeight = bounds.height * scaleY
    }

    const aspectRatio = srcWidth / srcHeight
    let destWidth = MINIMAP_WIDTH
    let destHeight = MINIMAP_WIDTH / aspectRatio

    if (destHeight > MINIMAP_HEIGHT) {
      destHeight = MINIMAP_HEIGHT
      destWidth = MINIMAP_HEIGHT * aspectRatio
    }

    const offsetX = (MINIMAP_WIDTH - destWidth) / 2
    const offsetY = (MINIMAP_HEIGHT - destHeight) / 2

    // Convert click to normalized coordinates
    const normalizedX = (clickX - offsetX) / destWidth
    const normalizedY = (clickY - offsetY) / destHeight

    if (normalizedX < 0 || normalizedX > 1 || normalizedY < 0 || normalizedY > 1) {
      return // Click outside thumbnail
    }

    // Convert to image coordinates
    let imageX, imageY
    if (bounds) {
      imageX = (bounds.x + normalizedX * bounds.width) / dimensions.width
      imageY = (bounds.y + normalizedY * bounds.height) / dimensions.height
    } else {
      imageX = normalizedX
      imageY = normalizedY
    }

    // Pan to clicked location
    const point = new (window as any).OpenSeadragon.Point(imageX, imageY)
    viewer.viewport.panTo(point, true)
  }

  return (
    <div
      ref={containerRef}
      className="absolute top-4 left-4 z-10"
      style={{
        width: `${MINIMAP_WIDTH}px`,
        height: `${MINIMAP_HEIGHT}px`,
        border: '2px solid #3B82F6',
        borderRadius: '4px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
        backgroundColor: '#000',
        overflow: 'hidden',
      }}
    >
      <canvas
        ref={canvasRef}
        width={MINIMAP_WIDTH}
        height={MINIMAP_HEIGHT}
        onClick={handleClick}
        style={{ cursor: 'pointer', display: 'block' }}
      />
      {!thumbnail && (
        <div className="absolute inset-0 flex items-center justify-center text-white text-xs">
          Loading...
        </div>
      )}
    </div>
  )
}

export default SimpleMinimap
